/**
 * IslandBarometer — premium pressure gauge overlay.
 * Polished SVG gauge with glass reflections, ambient glow, and smooth spring needle.
 * Portalled full-screen overlay triggered by tapping Buddy.
 */

import { memo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
import type { MacroDisplayBundle } from '@/lib/types/macroDisplay';

interface IslandBarometerProps {
  macro: MacroDisplayBundle;
  expanded: boolean;
  onClose: () => void;
  networkState?: string;
  blockState?: string;
  activeSignature?: string | null;
  heatLevel?: number;
  recentEventHeadline?: string | null;
  ltConfidence?: string;
}

const MACRO_ANGLES: Record<string, number> = {
  DEEP_VALUE: -108,
  ACCUMULATION: -60,
  NEUTRAL: 0,
  WARM: 60,
  HOT: 108,
};

const MACRO_LABEL_TEXT: Record<string, string> = {
  DEEP_VALUE: 'Deep Value',
  ACCUMULATION: 'Accumulation',
  NEUTRAL: 'Neutral',
  WARM: 'Warming Up',
  HOT: 'Overheated',
  MIXED: 'Mixed Signals',
};

const MACRO_COLORS: Record<string, { primary: string; glow: string; token: string }> = {
  DEEP_VALUE:    { primary: '#4a9eda', glow: 'rgba(74,158,218,0.35)',  token: 'hsl(var(--energy) / 0.75)' },
  ACCUMULATION:  { primary: '#5cb89a', glow: 'rgba(92,184,154,0.30)',  token: 'hsl(var(--success) / 0.65)' },
  NEUTRAL:       { primary: '#9a9585', glow: 'rgba(154,149,133,0.20)', token: 'hsl(var(--foreground) / 0.45)' },
  WARM:          { primary: '#d4913a', glow: 'rgba(212,145,58,0.35)',  token: 'hsl(var(--bitcoin) / 0.75)' },
  HOT:           { primary: '#c45c3e', glow: 'rgba(196,92,62,0.40)',   token: 'hsl(var(--stress) / 0.75)' },
  MIXED:         { primary: '#9a7abf', glow: 'rgba(154,122,191,0.25)', token: 'hsl(var(--mood) / 0.55)' },
};

// ── SVG geometry ──
const CX = 60;
const CY = 60;
const R = 44;
const ARC_START = 220;
const ARC_END = 320;

function polarToXY(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  const s = polarToXY(cx, cy, r, startDeg);
  const e = polarToXY(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
}

// 5 segments across the arc
const ARC_SEGMENTS = [
  { start: ARC_START,      end: ARC_START + 20, color: '#4a9eda', id: 'deep' },
  { start: ARC_START + 20, end: ARC_START + 40, color: '#5cb89a', id: 'acc' },
  { start: ARC_START + 40, end: ARC_END - 40,   color: '#8a8a7a', id: 'neut' },
  { start: ARC_END - 40,   end: ARC_END - 20,   color: '#d4913a', id: 'warm' },
  { start: ARC_END - 20,   end: ARC_END,         color: '#c45c3e', id: 'hot' },
];

// 5 tick marks at segment boundaries
const TICK_ANGLES = [ARC_START, ARC_START + 20, (ARC_START + ARC_END) / 2, ARC_END - 20, ARC_END];

function GaugeSVG({ needleRotate, glowColor }: { needleRotate: any; glowColor: string }) {
  const needleLen = 36;
  return (
    <svg width="100%" height="100%" viewBox="0 0 120 120" fill="none" className="drop-shadow-lg">
      <defs>
        {/* Gauge face gradient */}
        <radialGradient id="gaugeFace" cx="50%" cy="42%" r="50%">
          <stop offset="0%" stopColor="hsl(230 26% 18%)" />
          <stop offset="100%" stopColor="hsl(230 32% 11%)" />
        </radialGradient>
        {/* Glass highlight */}
        <radialGradient id="glassHighlight" cx="45%" cy="35%" r="40%">
          <stop offset="0%" stopColor="white" stopOpacity="0.06" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
        {/* Needle glow */}
        <filter id="needleGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Arc glow */}
        <filter id="arcGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.5" />
        </filter>
        {/* Ambient state glow */}
        <radialGradient id="ambientGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={glowColor} />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>

      {/* Ambient glow behind gauge */}
      <circle cx={CX} cy={CY} r="56" fill="url(#ambientGlow)" opacity="0.4" />

      {/* Outer bezel ring */}
      <circle cx={CX} cy={CY} r="54" fill="none" stroke="hsl(30 20% 22%)" strokeWidth="1.5" opacity="0.5" />
      <circle cx={CX} cy={CY} r="52" fill="none" stroke="hsl(30 15% 18%)" strokeWidth="0.5" opacity="0.3" />

      {/* Gauge face */}
      <circle cx={CX} cy={CY} r="50" fill="url(#gaugeFace)" />

      {/* Inner ring detail */}
      <circle cx={CX} cy={CY} r="50" fill="none" stroke="hsl(30 20% 25%)" strokeWidth="0.8" opacity="0.25" />

      {/* Glowing arc segments (behind main arcs) */}
      {ARC_SEGMENTS.map((seg) => (
        <path
          key={`glow-${seg.id}`}
          d={describeArc(CX, CY, R, seg.start, seg.end)}
          stroke={seg.color}
          strokeWidth="6"
          opacity="0.12"
          fill="none"
          strokeLinecap="round"
          filter="url(#arcGlow)"
        />
      ))}

      {/* Main colored arc segments */}
      {ARC_SEGMENTS.map((seg) => (
        <path
          key={seg.id}
          d={describeArc(CX, CY, R, seg.start, seg.end)}
          stroke={seg.color}
          strokeWidth="4"
          opacity="0.55"
          fill="none"
          strokeLinecap="round"
        />
      ))}

      {/* Tick marks */}
      {TICK_ANGLES.map((deg, i) => {
        const outer = polarToXY(CX, CY, R + 3, deg);
        const inner = polarToXY(CX, CY, R - 3, deg);
        return (
          <line
            key={i}
            x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y}
            stroke="hsl(30 15% 50%)" strokeWidth="0.8" opacity="0.4" strokeLinecap="round"
          />
        );
      })}

      {/* Minor tick marks between segments */}
      {ARC_SEGMENTS.map((seg) => {
        const mid = (seg.start + seg.end) / 2;
        const o = polarToXY(CX, CY, R + 1.5, mid);
        const inn = polarToXY(CX, CY, R - 1.5, mid);
        return (
          <line
            key={`minor-${seg.id}`}
            x1={inn.x} y1={inn.y} x2={o.x} y2={o.y}
            stroke="hsl(30 15% 40%)" strokeWidth="0.5" opacity="0.25" strokeLinecap="round"
          />
        );
      })}

      {/* Needle with glow */}
      <motion.g
        style={{ originX: `${CX}px`, originY: `${CY}px`, rotate: needleRotate }}
        filter="url(#needleGlow)"
      >
        <line
          x1={CX} y1={CY + 8} x2={CX} y2={CY - needleLen}
          stroke="#f7931a" strokeWidth="2" strokeLinecap="round"
        />
        {/* Needle tip accent */}
        <circle cx={CX} cy={CY - needleLen + 1} r="1.2" fill="#f7931a" opacity="0.8" />
      </motion.g>

      {/* Pivot cap */}
      <circle cx={CX} cy={CY} r="4.5" fill="hsl(230 26% 16%)" stroke="hsl(30 20% 30%)" strokeWidth="1" />
      <circle cx={CX} cy={CY} r="2.5" fill="#f7931a" />

      {/* Glass highlight overlay */}
      <circle cx={CX} cy={CY} r="49" fill="url(#glassHighlight)" />
    </svg>
  );
}

// ── Scale labels under gauge ──
function ScaleLabels() {
  return (
    <div className="flex items-center justify-between w-[180px] mx-auto -mt-1 mb-4">
      {[
        { text: 'Cold', color: '#4a9eda' },
        { text: '·', color: 'hsl(var(--foreground) / 0.12)' },
        { text: 'Neutral', color: '#8a8a7a' },
        { text: '·', color: 'hsl(var(--foreground) / 0.12)' },
        { text: 'Hot', color: '#c45c3e' },
      ].map((item, i) => (
        <span
          key={i}
          className={`font-body ${item.text === '·' ? 'text-[7px] font-light' : 'text-[7px] font-semibold uppercase tracking-[0.12em]'}`}
          style={{ color: item.color }}
        >
          {item.text}
        </span>
      ))}
    </div>
  );
}

// ── Confidence indicator ──
function ConfidenceIndicator({ confidence, accentColor }: { confidence: string; accentColor: string }) {
  const levels = ['LOW', 'MEDIUM', 'HIGH'] as const;
  const confIndex = levels.indexOf(confidence as typeof levels[number]);
  const label = confidence === 'HIGH' ? 'High confidence' : confidence === 'MEDIUM' ? 'Medium confidence' : 'Low confidence';

  return (
    <div className="flex items-center justify-center gap-2 pt-1">
      <div className="flex items-center gap-1">
        {levels.map((level, i) => (
          <div
            key={level}
            className="rounded-full transition-colors"
            style={{
              width: 5,
              height: 5,
              background: i <= confIndex ? accentColor : 'hsl(var(--foreground) / 0.06)',
            }}
          />
        ))}
      </div>
      <span
        className="text-[7px] font-body font-medium uppercase tracking-[0.1em]"
        style={{ color: 'hsl(var(--foreground) / 0.22)' }}
      >
        {label}
      </span>
    </div>
  );
}

function IslandBarometerInner({
  macro,
  expanded,
  onClose,
}: IslandBarometerProps) {
  const macroState = macro.state;
  const moodLabel = macro.moodLabel;
  const plainSubtitle = macro.plainSubtitle;
  const advisory = macro.advisory;

  const isMixed = macroState === 'MIXED';
  const baseAngle = MACRO_ANGLES[macroState] ?? 0;
  const colors = MACRO_COLORS[macroState] ?? MACRO_COLORS.NEUTRAL;

  const [wobble, setWobble] = useState(0);
  const rafRef = useRef(0);

  useEffect(() => {
    let running = true;
    const loop = (t: number) => {
      if (!running) return;
      if (isMixed) {
        setWobble(Math.sin((t / 3000) * Math.PI * 2) * 5);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { running = false; cancelAnimationFrame(rafRef.current); };
  }, [isMixed]);

  const needleAngle = baseAngle + (isMixed ? wobble : 0);
  const springAngle = useSpring(needleAngle, { stiffness: 80, damping: 14 });

  useEffect(() => { springAngle.set(needleAngle); }, [needleAngle, springAngle]);

  const needleRotate = useTransform(springAngle, (v) => v + 180);

  useEffect(() => {
    if (!expanded) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [expanded, onClose]);

  const labelText = MACRO_LABEL_TEXT[macroState] ?? 'Observing';

  return (
    <>
      {createPortal(
        <AnimatePresence>
          {expanded && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center pointer-events-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              onClick={() => onClose()}
            >
              {/* Backdrop */}
              <motion.div
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  background: 'hsl(var(--background) / 0.92)',
                  backdropFilter: 'blur(28px)',
                }}
              />

              {/* Content */}
              <motion.div
                initial={{ scale: 0.4, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.5, opacity: 0, y: 10 }}
                transition={{ duration: 0.45, ease: [0.23, 1, 0.32, 1] }}
                className="relative z-10 flex flex-col items-center px-6"
                style={{ width: 'min(340px, calc(100vw - 24px))' }}
                onClick={() => onClose()}
              >
                {/* Gauge */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
                  style={{ width: 200, height: 200 }}
                >
                  <GaugeSVG needleRotate={needleRotate} glowColor={colors.glow} />
                </motion.div>

                {/* Scale labels */}
                <ScaleLabels />

                {/* Primary: Mood label */}
                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18, duration: 0.35 }}
                  className="font-body font-semibold text-[17px] tracking-wide"
                  style={{ color: colors.token }}
                >
                  {moodLabel || labelText}
                </motion.p>

                {/* Macro state tag */}
                {moodLabel && moodLabel !== labelText && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.25 }}
                    className="text-[8px] font-body font-medium uppercase tracking-[0.15em] mt-1"
                    style={{ color: 'hsl(var(--foreground) / 0.22)' }}
                  >
                    {labelText}
                  </motion.p>
                )}

                {/* Plain subtitle */}
                {plainSubtitle && (
                  <motion.p
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25, duration: 0.3 }}
                    className="font-body font-medium text-[11.5px] leading-[1.6] mt-3 max-w-[260px] mx-auto text-center"
                    style={{ color: 'hsl(var(--foreground) / 0.55)' }}
                  >
                    {plainSubtitle}
                  </motion.p>
                )}

                {/* Divider */}
                <div
                  className="mx-auto mt-4 mb-4"
                  style={{
                    height: 1,
                    width: '45%',
                    background: `linear-gradient(90deg, transparent, ${colors.glow}, transparent)`,
                  }}
                />

                {/* Advisory block */}
                {advisory && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.32, duration: 0.4 }}
                    className="text-center space-y-3"
                  >
                    <p
                      className="text-[11px] font-body font-medium italic leading-relaxed"
                      style={{ color: 'hsl(var(--foreground) / 0.5)' }}
                    >
                      {advisory.oneLiner}
                    </p>

                    <p
                      className="text-[10px] font-body font-light leading-[1.7] max-w-[250px] mx-auto"
                      style={{ color: 'hsl(var(--foreground) / 0.36)' }}
                    >
                      {advisory.bubbleBody}
                    </p>

                    <ConfidenceIndicator confidence={advisory.confidence} accentColor={colors.token} />
                  </motion.div>
                )}

                {/* Footer */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-[7px] font-body font-light italic mt-6"
                  style={{ color: 'hsl(var(--foreground) / 0.12)' }}
                >
                  Observation only · not financial advice
                </motion.p>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.55 }}
                  className="text-[8px] font-body font-light mt-2.5"
                  style={{ color: 'hsl(var(--foreground) / 0.18)' }}
                  onClick={() => onClose()}
                >
                  tap to close
                </motion.p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}

const IslandBarometer = memo(IslandBarometerInner);
export default IslandBarometer;
